export const COMPROBANTE_ACCEPT = "image/jpeg,image/png,image/webp,image/jpg";

export const ATTACHMENT_ACCEPT = "image/jpeg,image/png,image/webp,image/jpg,application/pdf";

export const ALLOWED_COMPROBANTE_TYPES = ["image/jpeg", "image/jpg", "image/png", "image/webp"] as const;

export const ALLOWED_ATTACHMENT_TYPES = [
  "image/jpeg",
  "image/jpg",
  "image/png",
  "image/webp",
  "application/pdf",
] as const;

export const DEFAULT_MAX_SIZE_MB = 10;

export type FileValidationFailureCode = "too_large" | "unsupported_type";

export interface FileValidationFailure {
  valid: false;
  /** Developer-facing fallback. Consumers translate `code` instead of showing this. */
  error: string;
  code?: FileValidationFailureCode;
  /** Actual size, rounded to two decimals, when the file was refused for its size. */
  sizeMB?: number;
  maxSizeMB?: number;
}

export type FileValidationResult = { valid: true } | FileValidationFailure;

export const SPREADSHEET_EXTENSIONS = [".xlsx", ".csv"] as const;

export type SpreadsheetExtension = (typeof SPREADSHEET_EXTENSIONS)[number];

/**
 * Extensions first, MIME types second: Windows reports a CSV as `application/vnd.ms-excel` or as an empty
 * string, so an accept list built from MIME types alone hides the file in the picker.
 */
export const SPREADSHEET_ACCEPT =
  ".xlsx,.csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv";

export function spreadsheetExtension(fileName: string): SpreadsheetExtension | null {
  const lower = fileName.toLowerCase();
  return SPREADSHEET_EXTENSIONS.find((extension) => lower.endsWith(extension)) ?? null;
}

/**
 * Size and type check for a batch spreadsheet. Decides by extension, never by `File.type` — the browser's
 * MIME guess for CSV is unreliable across platforms and `.xlsx` arrives as a generic zip on some of them.
 */
export function validateSpreadsheetFile(
  file: File,
  maxSizeMB: number = DEFAULT_MAX_SIZE_MB,
): FileValidationResult {
  const maxSizeBytes = maxSizeMB * 1024 * 1024;

  if (file.size > maxSizeBytes) {
    return {
      valid: false,
      code: "too_large",
      sizeMB: Math.round((file.size / 1024 / 1024) * 100) / 100,
      maxSizeMB,
      error: `File exceeds ${maxSizeMB} MB`,
    };
  }

  if (!spreadsheetExtension(file.name)) {
    return {
      valid: false,
      code: "unsupported_type",
      error: "Only .xlsx or .csv files are accepted",
    };
  }

  return { valid: true };
}

export function validateComprobanteFile(
  file: File,
  maxSizeMB: number = DEFAULT_MAX_SIZE_MB,
): { valid: true } | { valid: false; error: string } {
  const maxSizeBytes = maxSizeMB * 1024 * 1024;

  if (file.size > maxSizeBytes) {
    const sizeMB = (file.size / 1024 / 1024).toFixed(2);
    return {
      valid: false,
      error: `El archivo es muy grande (${sizeMB} MB). Máximo permitido: ${maxSizeMB} MB`,
    };
  }

  if (!ALLOWED_COMPROBANTE_TYPES.includes(file.type as (typeof ALLOWED_COMPROBANTE_TYPES)[number])) {
    return {
      valid: false,
      error: "Solo se permiten imágenes (JPG, PNG, WebP).",
    };
  }

  return { valid: true };
}

export function validateAttachmentFile(
  file: File,
  maxSizeMB: number = DEFAULT_MAX_SIZE_MB,
): { valid: true } | { valid: false; error: string } {
  const maxSizeBytes = maxSizeMB * 1024 * 1024;

  if (file.size > maxSizeBytes) {
    const sizeMB = (file.size / 1024 / 1024).toFixed(2);
    return {
      valid: false,
      error: `El archivo es muy grande (${sizeMB} MB). Máximo permitido: ${maxSizeMB} MB`,
    };
  }

  if (!ALLOWED_ATTACHMENT_TYPES.includes(file.type as (typeof ALLOWED_ATTACHMENT_TYPES)[number])) {
    return {
      valid: false,
      error: "Solo se permiten imágenes (JPG, PNG, WebP) o PDF.",
    };
  }

  return { valid: true };
}
